import { NotificationOrigin, NotificationType, UploadState } from '@synergy/models';
import { createSelector } from 'reselect';
import { errorsToTouch, nullIfEmpty } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { CustomerDetailOverviewFormValues } from '../models/customer-detail-overview-form-values';
import { CustomerDetailOverviewRequest } from '../api/models/customer-detail-overview-request';

const customerDetailOverviewState = (state: AppState) =>
    state.contactsAndOrganizations.customers.customerDetail.overview;
export const customerDetailOverviewSelector = createSelector(customerDetailOverviewState, state => state);

export const customerSelector = createSelector(
    customerDetailOverviewState,
    detailOverviewState => detailOverviewState.customer
);

export const customerDetailOverviewBankAccountsSelector = createSelector(
    customerDetailOverviewState,
    detailOverviewState => ({
        formValues: detailOverviewState.bankAccounts.map(bankAccount => ({
            ...bankAccount,
            media: bankAccount.media.map(m => ({ ...m, state: UploadState.Finished })),
        })),
        errors: detailOverviewState.bankAccountErrors,
    })
);

/**
 * The defaults the mask fills in for values the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159): compared against raw server
 * values, every one of these defaults would read as something the user typed.
 */
const withDefaults = (customer?: CustomerDetailOverviewFormValues): CustomerDetailOverviewFormValues =>
    ({
        ...customer,
        tags: customer?.tags ?? [],
        classifications: customer?.classifications ?? [],
        website: customer?.website ?? '',
        blacklistedSuppliers: customer?.blacklistedSuppliers ?? [],
        addresses:
            customer?.addresses.map(address => ({
                ...address,
                costCentre: address.costCentre ?? '',
                region: address.region ?? undefined,
            })) ?? [],
        categoryIds: customer?.categoryIds ?? [],
        mainEmail: customer?.mainEmail ?? '',
        emails: customer?.emails ?? [],
        mainPhone: customer?.mainPhone ?? '',
        phones: customer?.phones ?? [],
        name: customer?.name ?? '',
        uid: customer?.uid ?? '',
        glnNumber: customer?.glnNumber ?? '',
        internalNotes: customer?.internalNotes ?? '',
        placeOfJurisdiction: customer?.placeOfJurisdiction ?? '',
        registerNumber: customer?.registerNumber ?? '',
        isMain: customer?.isMain ?? false,
        requireOrderNumber: customer?.requireOrderNumber ?? false,
        financeAccountDebitor: customer?.financeAccountDebitor ?? '',
        financeAccountCreditor: customer?.financeAccountCreditor ?? '',
        notificationSettings: customer?.notificationSettings ?? [
            {
                notificationOrigin: NotificationOrigin.PurchaseOrderManual,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.PurchaseOrderShop,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.Demand,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.Inquiry,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.Invoice,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
        ],
    } as CustomerDetailOverviewFormValues);

export const customerDetailOverviewFormDataSelector = createSelector(
    customerDetailOverviewState,
    detailOverviewState => {
        const { customer, overviewErrors: errors } = detailOverviewState;

        return {
            formValues: withDefaults(customer),
            errors,
            touched: errorsToTouch(errors),
        };
    }
);

/** What the mask compares against to decide whether anything is unsaved. */
export const customerDetailOverviewBaselineSelector = createSelector(customerDetailOverviewState, detailOverviewState =>
    detailOverviewState.baseline ? withDefaults(detailOverviewState.baseline) : undefined
);

export const customerDetailOverviewRequestSelector = createSelector(
    customerDetailOverviewState,
    detailOverviewState => {
        const customer = detailOverviewState.customer;

        if (!customer) {
            return;
        }

        const {
            glnNumber,
            parent,
            uid,
            registerNumber,
            placeOfJurisdiction,
            internalNotes,
            blacklistedSuppliers,
            responsibleContact,
            tags,
            classifications,
            ...data
        } = customer;

        const phones = data.phones.map(phone => ({
            ...phone,
            id: phone.id && phone.id < 0 ? undefined : phone.id,
        }));

        const emails = data.emails.map(email => ({
            ...email,
            id: email.id && email.id < 0 ? undefined : email.id,
        }));

        const addresses = data.addresses.map(address => ({
            ...address,
            id: address.id && address.id < 0 ? undefined : address.id,
            country: address.country?.code,
            costCentre: nullIfEmpty(address.costCentre),
            regionCode: address.region?.code,
        }));

        const notifications = data.notificationSettings.map(notification => ({
            id: notification.id && notification.id < 0 ? undefined : notification.id,
            recipientEmail: notification.overrideEmail ? notification.recipientEmail : null,
            notificationType: notification.notificationType,
            notificationOrigin: notification.notificationOrigin,
        }));

        const request: CustomerDetailOverviewRequest = {
            name: data.name,
            glnNumber: nullIfEmpty(glnNumber),
            parentId: parent?.id ?? null,
            uid: nullIfEmpty(uid),
            registerNumber: nullIfEmpty(registerNumber),
            placeOfJurisdiction: nullIfEmpty(placeOfJurisdiction),
            internalNotes: nullIfEmpty(internalNotes),
            blacklistedSupplierIds: blacklistedSuppliers?.map(supplier => supplier.id) ?? [],
            responsibleContactId: responsibleContact?.id ?? null,
            mainEmail: data.mainEmail ?? '',
            emails,
            tagIds: tags?.map(tag => tag.id) ?? [],
            classificationIds: classifications?.map(classification => classification.id) ?? [],
            website: nullIfEmpty(data.website),
            categoryIds: data?.categoryIds ?? [],
            mainPhone: nullIfEmpty(data?.mainPhone),
            phones,
            addresses,
            notificationSettings: notifications,
            isMain: data.isMain,
            requireOrderNumber: data.requireOrderNumber,
            financeAccountDebitor: nullIfEmpty(data.financeAccountDebitor),
            financeAccountCreditor: nullIfEmpty(data.financeAccountCreditor),
        };

        return request;
    }
);
